import { TokenManager } from "@/supabase/supabaseClient";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export interface UploadResponse {
  url: string;
  path: string;
  fileName: string;
}

export class FileUploadService {
  private static readonly BUCKET_NAME = "task-files";
  private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  private static readonly ALLOWED_TYPES = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/x-icon",
    "image/vnd.microsoft.icon",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
  ];

  /**
   * Upload a file to Supabase Storage
   */
  static async uploadFile(
    file: File,
    folder: string = "general"
  ): Promise<UploadResponse> {
    try {
      // Validate file
      this.validateFile(file);

      // Ensure token is ready before making storage operation
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot upload file");
        throw new Error("Authentication token not available");
      }

      // Generate unique filename
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2)}.${fileExt}`;
      const filePath = `${folder}/${fileName}`;

      // Get the token for the authenticated upload
      const token = TokenManager.getStoredToken();
      if (!token) {
        throw new Error("Authentication token not available");
      }

      // Upload to Supabase Storage using fetch
      const uploadResponse = await fetch(
        `${supabaseUrl}/storage/v1/object/${this.BUCKET_NAME}/${filePath}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "3600",
            "Content-Type": file.type,
          },
          body: file,
        }
      );

      if (!uploadResponse.ok) {
        const error = await uploadResponse.text();
        console.error("Upload failed:", error);
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      // Get public URL
      const publicUrl = `${supabaseUrl}/storage/v1/object/public/${this.BUCKET_NAME}/${filePath}`;

      return {
        url: publicUrl,
        path: filePath,
        fileName: file.name,
      };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      console.error("Error in uploadFile:", errorMessage);
      throw new Error(`File upload failed: ${errorMessage}`);
    }
  }

  /**
   * Upload multiple files
   */
  static async uploadFiles(
    files: File[],
    folder: string = "general"
  ): Promise<UploadResponse[]> {
    const uploadPromises = files.map((file) => this.uploadFile(file, folder));
    return Promise.all(uploadPromises);
  }

  /**
   * Delete a file from storage
   */
  static async deleteFile(filePath: string): Promise<void> {
    try {
      // Ensure token is ready before making storage operation
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot delete file");
        throw new Error("Authentication token not available");
      }

      // Get the token for the authenticated delete
      const token = TokenManager.getStoredToken();
      if (!token) {
        throw new Error("Authentication token not available");
      }

      const deleteResponse = await fetch(
        `${supabaseUrl}/storage/v1/object/${this.BUCKET_NAME}/${filePath}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!deleteResponse.ok) {
        const error = await deleteResponse.text();
        console.error("Delete failed:", error);
        throw new Error(`Delete failed: ${deleteResponse.statusText}`);
      }
    } catch (error) {
      console.error("Error in deleteFile:", error);
      throw error;
    }
  }

  /**
   * Upload task attachment
   */
  static async uploadTaskAttachment(
    taskId: string,
    file: File
  ): Promise<UploadResponse> {
    return this.uploadFile(file, `tasks/${taskId}/attachments`);
  }

  /**
   * Upload task photo
   */
  static async uploadTaskPhoto(
    taskId: string,
    file: File
  ): Promise<UploadResponse> {
    // Validate it's an image
    if (!file.type.startsWith("image/")) {
      throw new Error("Only image files are allowed for photos");
    }

    return this.uploadFile(file, `tasks/${taskId}/photos`);
  }

  /**
   * Upload step photo
   */
  static async uploadStepPhoto(
    taskId: string,
    stepId: string,
    file: File
  ): Promise<UploadResponse> {
    // Validate it's an image
    if (!file.type.startsWith("image/")) {
      throw new Error("Only image files are allowed for photos");
    }

    return this.uploadFile(file, `tasks/${taskId}/steps/${stepId}/photos`);
  }

  /**
   * Validate file before upload
   */
  private static validateFile(file: File): void {
    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(
        `File size must be less than ${this.MAX_FILE_SIZE / 1024 / 1024}MB`
      );
    }

    // Check file type
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      throw new Error(`File type ${file.type} is not allowed`);
    }
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  /**
   * Get file extension from filename
   */
  static getFileExtension(filename: string): string {
    return filename.split(".").pop()?.toLowerCase() || "";
  }

  /**
   * Check if file is an image
   */
  static isImage(file: File): boolean {
    return file.type.startsWith("image/");
  }

  /**
   * Create a preview URL for images
   */
  static createImagePreview(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.isImage(file)) {
        reject(new Error("File is not an image"));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}
